/**
 * CSV export for report tables (#2712).
 *
 * The reports page promised a CSV that was never produced — the handler was a
 * toast and nothing else. The data is already in the browser, so the export
 * is built client-side rather than added as a backend endpoint.
 *
 * Two Finnish-locale details this gets right once, so call sites do not each
 * have to:
 *
 * - **Semicolon delimiter.** Excel in a Finnish locale splits on `;`, not `,`.
 *   A comma-delimited file opens as a single column per row.
 * - **Comma decimal separator.** `12,50` is a number to Finnish Excel;
 *   `12.50` is text, and text does not sum.
 *
 * The BOM is what makes Excel read the file as UTF-8; without it ä and ö
 * arrive mojibaked.
 */

/** A value a cell may hold. `null`/`undefined` become an empty cell. */
export type CsvValue = string | number | null | undefined;

const DELIMITER = ';';
const BOM = '﻿';

/**
 * Quote a field when it contains something that would otherwise break the
 * row, doubling any embedded quote as RFC 4180 requires.
 */
function escapeField(value: CsvValue): string {
  if (value === null || value === undefined) return '';
  const text = typeof value === 'number' ? formatNumber(value) : String(value);
  if (text === '') return '';
  return /[";\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

/** Finnish decimal separator, and no thousands separator (Excel adds its own). */
function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  return Number.isInteger(value) ? String(value) : String(value).replace('.', ',');
}

/** Render rows as a CSV string. Exported for testing without a DOM. */
export function toCsv(headers: string[], rows: CsvValue[][]): string {
  return [headers, ...rows].map((row) => row.map(escapeField).join(DELIMITER)).join('\r\n');
}

/**
 * Build the CSV and hand it to the browser as a download.
 *
 * `filename` is used as given; callers add the date range so a user who
 * exports twice does not overwrite the first file.
 */
export function downloadCsv(filename: string, headers: string[], rows: CsvValue[][]): void {
  const blob = new Blob([BOM + toCsv(headers, rows)], { type: 'text/csv;charset=utf-8;' });
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = filename.endsWith('.csv') ? filename : `${filename}.csv`;
  link.click();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
}
